import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { emailWithTldValidator } from 'src/app/validators/email-tld.validator';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IUser } from 'src/app/models/user.model';
import { IError } from 'src/app/models/error.model';
import { UserService } from 'src/app/services/user.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { ContextService } from 'src/app/services/context.service';
import { IClinicContext } from 'src/app/models/clinic-context.model';
import { forkJoin } from 'rxjs';
import { generatePassword, copyToClipboard } from 'src/app/utils/password.utils';

@Component({
  selector: 'app-user-edit-v2',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.sass']
})
export class UserEditV2Component implements OnInit {
  loading = false;
  userForm!: FormGroup;
  userId: string | null = '';
  hasToShowPermissionField = true;

  // Password reset modal
  resetPasswordModalOpen = false;
  resetPasswordStep: 'confirm' | 'loading' | 'success' = 'confirm';
  resetPasswordValue = '';
  resetPasswordCopied = false;

  // Clinics multi-select
  availableClinics: IClinicContext[] = [];
  selectedClinicIds: string[] = [];
  clinicsDropdownOpen = false;
  activeClinicHasGroup = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<{ warningPopup: IError }>,
    private contextService: ContextService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.route.paramMap.subscribe(params => {
      this.userId = params.get('userId');
      if (this.userId) {
        this.loadUser();
      }
    });
  }

  initForm(): void {
    this.userForm = this.fb.group({
      name: ['', [Validators.required]],
      // Enable email input so client/server validation messages can be shown
      email: ['', [Validators.required, emailWithTldValidator()]],
      is_blocked: ['false', [Validators.required]],
      permission: ['', [Validators.required]]
    });
  }

  loadUser(): void {
    this.loading = true;

    // Carrega clínicas e dados do usuário em paralelo, aguarda ambos
    forkJoin({
      clinics: this.contextService.loadClinicsAndSetDefault(),
      user: this.userService.getUserById(this.userId!)
    }).subscribe({
      next: ({ user }) => {
        // Popula clínicas disponíveis
        const allClinics = this.contextService.getAvailableClinics();
        const activeClinic = this.contextService.getActiveClinicSnapshot();
        this.activeClinicHasGroup = !!activeClinic?.groupId;
        if (activeClinic?.groupId) {
          this.availableClinics = allClinics.filter(c => c.groupId === activeClinic.groupId);
        } else {
          this.availableClinics = allClinics.filter(c => c.cnpj === activeClinic?.cnpj);
        }

        // Popula formulário
        this.userForm.patchValue({
          name: user.name,
          email: user.email,
          is_blocked: user.is_blocked ? 'true' : 'false',
          permission: user.permission
        });
        try {
          this.userForm.get('email')?.disable();
        } catch (e) {
          // ignore
        }

        // Carrega vínculos de clínicas (availableClinics já está populado)
        this.loadUserAccess();
      },
      error: (error) => {
        console.error('Erro ao carregar usuário:', error);
        this.loading = false;
      }
    });
  }

  loadUserAccess(): void {
    this.userService.getUserAccess(this.userId!).subscribe({
      next: (access) => {
        if (access?.clinics && access.clinics.length > 0) {
          // Normaliza IDs para corresponder aos IDs de availableClinics (match por id ou cnpj)
          this.selectedClinicIds = access.clinics
            .map((c: any) => {
              const rawId = c.id || c.cnpj || c;
              const match = this.availableClinics.find(ac => ac.id === rawId || ac.cnpj === rawId);
              return match ? match.id : rawId;
            })
            .filter((id: string) => this.availableClinics.some(ac => ac.id === id));
        } else {
          this.preSelectActiveClinic();
        }
        this.loading = false;
      },
      error: () => {
        this.preSelectActiveClinic();
        this.loading = false;
      }
    });
  }

  /** Pré-seleciona a clínica ativa como vínculo inicial */
  private preSelectActiveClinic(): void {
    const activeClinic = this.contextService.getActiveClinicSnapshot();
    if (activeClinic) {
      const clinicId = activeClinic.id || activeClinic.cnpj;
      if (clinicId && !this.selectedClinicIds.includes(clinicId)) {
        this.selectedClinicIds = [clinicId];
      }
    }
  }

  // ─── Clinics Multi-Select ────────────────────────────────────────────

  toggleClinicSelection(clinicId: string): void {
    const index = this.selectedClinicIds.indexOf(clinicId);
    if (index > -1) {
      this.selectedClinicIds.splice(index, 1);
    } else {
      this.selectedClinicIds.push(clinicId);
    }
  }

  isClinicSelected(clinicId: string): boolean {
    return this.selectedClinicIds.includes(clinicId);
  }

  get selectedClinicsNames(): string {
    if (this.selectedClinicIds.length === 0) return '';
    return this.availableClinics
      .filter(c => this.selectedClinicIds.includes(c.id))
      .map(c => c.name)
      .join(', ');
  }

  get selectedClinicsForDefault(): IClinicContext[] {
    return this.availableClinics.filter(c => this.selectedClinicIds.includes(c.id));
  }

  toggleClinicsDropdown(): void {
    this.clinicsDropdownOpen = !this.clinicsDropdownOpen;
  }

  onCancel(): void {
    this.router.navigate(['main', 'v2', 'users']);
  }

  // ─── Password Reset Modal ────────────────────────────────────────────

  openResetPasswordModal(): void {
    this.resetPasswordModalOpen = true;
    this.resetPasswordStep = 'confirm';
    this.resetPasswordValue = '';
    this.resetPasswordCopied = false;
  }

  closeResetPasswordModal(): void {
    this.resetPasswordModalOpen = false;
    this.resetPasswordStep = 'confirm';
    this.resetPasswordValue = '';
    this.resetPasswordCopied = false;
  }

  onResetModalBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && this.resetPasswordStep !== 'loading') {
      this.closeResetPasswordModal();
    }
  }

  confirmResetPassword(): void {
    if (!this.userId) return;
    this.resetPasswordStep = 'loading';
    this.resetPasswordValue = generatePassword();

    this.userService.resetPassword(this.userId, this.resetPasswordValue).subscribe({
      next: () => {
        this.resetPasswordStep = 'success';
      },
      error: (error) => {
        this.resetPasswordModalOpen = false;
        this.resetPasswordStep = 'confirm';
        const message = error?.error?.message || error?.message || 'Erro ao resetar senha.';
        this.store.dispatch(setWarningText(message, error?.status || 0));
      }
    });
  }

  async copyResetPassword(): Promise<void> {
    if (this.resetPasswordValue) {
      const success = await copyToClipboard(this.resetPasswordValue);
      if (success) {
        this.resetPasswordCopied = true;
        setTimeout(() => this.resetPasswordCopied = false, 2000);
      }
    }
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.store.dispatch(setWarningText('Preencha e valide todos os campos obrigatórios.'));
      return;
    }

    const selectedPermission = this.userForm.get('permission')?.value;

    // ADMINs recebem acesso a todas as clínicas do grupo automaticamente pelo backend
    if (selectedPermission === 'ADMIN') {
      const activeClinic = this.contextService.getActiveClinicSnapshot();
      if (activeClinic) {
        const clinicId = activeClinic.id || activeClinic.cnpj;
        this.selectedClinicIds = [clinicId];
      }
    } else if (this.activeClinicHasGroup) {
      // Validação de clínicas (apenas quando a clínica ativa pertence a um grupo)
      if (this.selectedClinicIds.length === 0) {
        this.store.dispatch(setWarningText('Selecione pelo menos uma clínica.'));
        return;
      }
    } else {
      // Sem grupo — vincula automaticamente à clínica ativa
      const activeClinic = this.contextService.getActiveClinicSnapshot();
      if (activeClinic) {
        const clinicId = activeClinic.id || activeClinic.cnpj;
        this.selectedClinicIds = [clinicId];
      }
    }

    this.loading = true;
    const formValue = this.userForm.getRawValue();

    const userRequest: IUser = {
      name: formValue.name?.trim() || '',
      email: formValue.email?.trim().toLowerCase() || '',
      permission: formValue.permission || '',
      is_blocked: formValue.is_blocked === 'true'
    };

    this.userService.updateUser(this.userId || '', userRequest).subscribe({
      next: () => {
        // Atualizar status se necessário
        this.userService.changeStatus(this.userId || '', userRequest.is_blocked).subscribe({
          next: () => {
            // Atualizar vínculos de clínicas
            this.userService.updateUserAccess(this.userId || '', this.selectedClinicIds, '').subscribe({
              next: () => {
                this.loading = false;
                this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
                  this.router.navigate(['main', 'v2', 'users']);
                });
              },
              error: (error) => {
                console.error('Erro ao atualizar vínculos de clínicas:', error);
                this.loading = false;
                this.store.dispatch(setWarningText('Usuário atualizado, mas houve erro ao vincular clínicas.'));
                this.router.navigate(['main', 'v2', 'users']);
              }
            });
          },
          error: (error) => {
            console.error('Erro ao atualizar status:', error);
            this.loading = false;
          }
        });
      },
      error: (error) => {
        console.error('Erro ao atualizar usuário:', error);
        // Show inline error on email field when API returns message (e.g. "Email já cadastrado.")
        const message = (error as any)?.error?.message || (error as any)?.message || 'Erro ao atualizar usuário.';
        try {
          this.userForm.get('email')?.setErrors({ api: message });
        } catch (e) {
          // ignore if form not initialized
        }
        this.store.dispatch(setWarningText(message, (error as any)?.status || 0));
        this.loading = false;
      }
    });
  }
}
